import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { FaArrowLeftLong } from "react-icons/fa6";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";

import Button from "../../../components/common/Button/Button";
import ReusableMuiTable from "../../../components/common/Table/Table";
import FormInput from "../../../components/common/FormInput/FormInput";
import { showToast } from "../../../components/common/ToastNotification/ToastNotification";

import {
  getProductSpecifications,
  addProductSpecification,
  updateProductSpecification,
  getSpecificationsById,
  deleteProductSpecification,
} from "../../../api/service/products.service";

const ProductSpecification = () => {
  const { id } = useParams();

  const [specifications, setSpecifications] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formValue, setFormValue] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [searchspecification, setSearchSpecification] = useState("");

  const fetchSpecifications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getProductSpecifications(id, page, 10, searchspecification);
      if (res?.success) {
        setSpecifications(res.data.data || []);
        setTotalPages(res.data.last_page || 0);
      }
    } catch {
      showToast.error("Failed to load specifications");
    } finally {
      setLoading(false);
    }
  }, [id, page, searchspecification]);

  useEffect(() => {
    if (id) fetchSpecifications();
  }, [fetchSpecifications, id]);

  const handleEdit = async (row) => {
    try {
      setLoading(true);
      const res = await getSpecificationsById(row.id);
      if (res?.success) {
        setFormValue(res.data.text);
        setEditingId(row.id);
      }
    } catch {
      showToast.error("Failed to load specification");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This specification will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);

      const res = await deleteProductSpecification(row.id);

      if (res?.success) {
        setSpecifications((prev) =>
          prev.filter((item) => item.id !== row.id)
        );

        Swal.fire({
          title: "Deleted!",
          text: "Product specification has been deleted successfully.",
          icon: "success",
          confirmButtonColor: "#C39B22",
        });
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Delete failed. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const value = formValue.trim();
    if (!value) {
      setError("Specification cannot be empty");
      return;
    }

    if (editingId) {
      setSpecifications((prev) =>
        prev.map((item) =>
          item.id === editingId ? { ...item, text: value } : item
        )
      );
    } else {
      setSpecifications((prev) => [
        {
          id: `temp-${Date.now()}`,
          text: value,
        },
        ...prev,
      ]);
    }

    setFormValue("");
    setEditingId(null);
    setError("");

    try {
      setLoading(true);

      let res;
      if (editingId) {
        res = await updateProductSpecification({
          product_specification_id: editingId,
          text: value,
        });
      } else {
        res = await addProductSpecification({
          product_id: id,
          text: value,
        });
      }

      showToast.success(res?.message || "Specification saved");

      fetchSpecifications();
    } catch (error) {
      showToast.error(error?.message || "Action failed");

      fetchSpecifications();
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { name: "text", label: "Specification" },
    {
      name: "actions",
      label: "Action",
      options: {
        sort: false,
        setCellProps: () => ({
          style: { textAlign: "right", width: 160 },
        }),
        customBodyRenderLite: (i) => {
          const row = specifications[i];
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip title="Edit">
                <IconButton
                  onClick={() => handleEdit(row)}
                  sx={{ color: "#C39B22", p: 0.5, minWidth: "auto" }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  onClick={() => handleDelete(row)}
                  sx={{ color: "#d32f2f", p: 0.5, minWidth: "auto" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    },
  ];

  return (
    <div className="py-3">
      <div
        className="d-flex align-items-center gap-2 mb-3"
        onClick={() => window.history.back()}
        style={{ cursor: "pointer" }}
      >
        <FaArrowLeftLong />
        <h3 className="mb-0 management-tile ml-2">Add / Edit Product Specification</h3>
      </div>

      <Row className="mb-3">
        <Col xs={12}>
          <FormInput
            value={formValue}
            onChange={(e) => {
              setFormValue(e.target.value);
              setError("");
            }}
            placeholder="Type specification..."
            disabled={loading}
          />
          {error && <small className="text-danger">{error}</small>}
        </Col>

        <Col xs={3} className="mt-2">
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !formValue.trim()}
          >
            {editingId ? "Update" : "Add"}
          </Button>
        </Col>
      </Row>

      <ReusableMuiTable
        title="Product Specifications....."
        data={specifications}
        columns={columns}
        loading={loading}
        options={{
          search: true,
          serverSide: true,
          page: page - 1,
          count: totalPages * 10,
          onChangePage: (p) => setPage(p + 1),
          selectableRows: "none",
          onSearchChange: (searchText) => {
            setSearchSpecification(searchText || "");
            setPage(1);
          },
          onSearchClose: () => {
            setSearchSpecification("");
            setPage(1);
          },
        }}
      />
    </div>
  );
};

export default ProductSpecification;